'use client';

import { useCallback, useRef } from 'react';

import { logger } from '@/lib/logger';

import type { RefreshUserOutcome, SwitchWorkspaceOutcome } from './outcomes';

/**
 * 組織・ワークスペースの切り替え（Issue #8078 で `AuthProvider` から切り出し）。
 *
 * ## なぜ分けるのか
 *
 * `AuthProvider` は Context の構築に加えて、初回ロード・`/api/auth/me` の検証・切り替えの
 * 追い越し制御を全部持っていて 644 行あった（`src/components/CLAUDE.md` の 500 行基準超え）。
 * 切り替えは #8030（追い越し制御）と #8076（取得失敗の扱い）でコメントが厚くなった塊で、
 * ここだけで 160 行ある。
 *
 * **挙動は変えていない。** #8030 / #8076 で入れた不変条件は次の 3 つで、既存テスト
 * （`AuthProvider.switchWorkspace.test.tsx` 13 件 / `AuthProvider.meValidation.test.tsx` 8 件）が
 * 守っている。
 *
 * 1. 追い越された要求は状態を書かない
 * 2. 検証に通らない応答は保存しない
 * 3. 引き直しが通れば切り替えを成立させる
 *
 * ## 状態は持たない
 *
 * この hook は**世代番号だけ**を自前で持ち、ユーザー状態は `refreshUser` を通して
 * 呼び出し元（`AuthProvider`）に委ねる。両方が state を持つと、どちらが正本か分からなく
 * なる ── それが元の 644 行の読みにくさの一因だった。
 */

/**
 * 切り替え先を JWT（NextAuth セッション）へ反映する。
 *
 * Issue #659: `session.update()` が完了した時点で JWT Cookie も更新済みになる。
 * 失敗の文言をここに閉じ込めるのは、`switchWorkspace` 本体が追い越し判定（Issue #8030）で
 * 分岐を多く持つようになり、1 つの関数で追いにくくなったため。
 */
async function updateSessionWorkspace(
  update: (data: { organizationId: string; workspaceId: string }) => Promise<unknown>,
  user: { currentOrganizationId: string; currentWorkspaceId: string },
): Promise<void> {
  try {
    await update({
      organizationId: user.currentOrganizationId,
      workspaceId: user.currentWorkspaceId,
    });
  } catch (error) {
    logger.error('[switchWorkspace] JWT update failed:', error);
    throw new Error('セッションの更新に失敗しました');
  }
}

/**
 * 切り替え後のユーザー取得だけが落ちて、**引き直しで回復した**か（Issue #8076 レビュー指摘）。
 *
 * ここで失敗として返すと、認証状態は新しいワークスペースなのに呼び出し元は失敗を受け取り、
 * `workspaceChanged` も出ないという食い違いになる。`/api/auth/me` の一時的な失敗で
 * 切り替えを取り消さない。
 */
function recoveredFromUnavailableUser(error: unknown, retried: RefreshUserOutcome): boolean {
  return error instanceof PostSwitchUserUnavailableError && retried === 'applied';
}

/**
 * 切り替え自体は通ったが、その後のユーザー情報取得が使える形でなかった（Issue #8076）。
 *
 * **他の失敗と区別する。** この失敗だけは、`catch` 側の引き直しが通れば切り替えを
 * 成立させてよい（セッションは既に新しいワークスペースを指している）。
 */
class PostSwitchUserUnavailableError extends Error {
  constructor() {
    super('ワークスペースの切り替え後にユーザー情報を取得できませんでした');
    this.name = 'PostSwitchUserUnavailableError';
  }
}

/**
 * ワークスペース切り替え API の失敗を、利用者向けの文言へ写す（Issue #3071）。
 *
 * 成功なら何もしない。`switchWorkspace` 本体から切り出したのは、追い越し判定
 * （Issue #8030）を足したぶん分岐が増え、1 つの関数で追いにくくなったため。
 */
/**
 * 前の切り替え送信を待つ上限（Issue #8122 レビュー指摘）。
 *
 * 前の送信が応答も失敗もせず止まると、以後の切り替えが全部その後ろで止まる（Vercel の
 * 関数タイムアウトまで数分）。上限を過ぎたら待たずに送る。
 */
// ponytail: 上限超えの遅い要求が後から DB を上書きする余地は残る。閉じるならサーバ側の世代/条件付き更新
export const SWITCH_QUEUE_WAIT_MS = 10_000;

/** `previous` の決着か `ms` の経過の、早い方で解決する。 */
function waitAtMost(previous: Promise<unknown>, ms: number): Promise<unknown> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise((resolve) => {
    timer = setTimeout(resolve, ms);
  });
  return Promise.race([previous, timeout]).finally(() => clearTimeout(timer));
}

function throwForFailedWorkspaceSwitch(response: Response): void {
  if (response.ok) return;
  if (response.status === 403) {
    throw new Error('このワークスペースへのアクセス権限がありません');
  }
  if (response.status === 404) {
    throw new Error('ワークスペースが見つかりません');
  }
  throw new Error('ワークスペースの切り替えに失敗しました');
}

/**
 * 切り替え応答から、JWT へ書く ID を取り出す（Issue #8078 レビュー指摘）。
 *
 * **受け入れられなければ `null`。** `AuthenticatedUser` の型は広く（どちらも optional）、
 * この経路が必要とするのは「使える ID が入っていること」だけ。型の広さに頼らず、ここで絞る。
 *
 * ## `null` への切り替えでは `currentWorkspaceId` の欠落を許す
 *
 * `switchWorkspace(null)` は「組織レベルへ切り替える（ワークスペースなし）」という正規の
 * 入力で、API 側も `switchOrganization` へ振る。組織にワークスペースが無ければ
 * `currentWorkspaceId: undefined` が返り、**JSON からキーごと落ちる**。
 *
 * ここで一律に文字列を要求すると、その**正常な応答を失敗として扱う**（レビュー指摘）。
 * 呼び出し元には例外が伝わり、`workspaceChanged` も出ないので、切り替わったのに画面が
 * 追随しない。要求が `null` のときだけ欠落を受け入れる。
 */
function toSwitchedUser(
  data: unknown,
  requestedWorkspaceId: string | null,
): { currentOrganizationId: string; currentWorkspaceId: string } | null {
  const user =
    typeof data === 'object' && data !== null ? (data as Record<string, unknown>).user : undefined;
  if (typeof user !== 'object' || user === null) return null;
  const { currentOrganizationId, currentWorkspaceId } = user as Record<string, unknown>;
  if (typeof currentOrganizationId !== 'string') return null;

  if (requestedWorkspaceId === null) {
    // 組織レベルへの切り替え。**値が来ていても無視して空にする** ── 要求が「ワークスペース
    // なし」なのに、応答の値を JWT へ書くとどこかのワークスペースに居ることになる。
    return { currentOrganizationId, currentWorkspaceId: '' };
  }
  if (typeof currentWorkspaceId !== 'string') return null;
  return { currentOrganizationId, currentWorkspaceId };
}

export interface UseAuthSwitchArgs {
  /** `/api/auth/me` を引き直して状態へ反映する。`shouldApply` が false を返したら書かない。 */
  refreshUser: (shouldApply?: () => boolean) => Promise<RefreshUserOutcome>;
  /** NextAuth の `update`。JWT（Cookie）を新しい組織・ワークスペースへ進める。 */
  update: (data: { organizationId: string; workspaceId: string }) => Promise<unknown>;
  /** React Query のキャッシュ。切り替えで前の組織・WS のものを残さない。 */
  queryClient: { clear: () => void };
  /** React Query に乗っていない Valtio 側の会話一覧。同じ理由でクリアする。 */
  conversationUIActions: { resetForWorkspaceSwitch: () => void };
  /**
   * **セッションが動き始めることを知らせる**（Issue #8129）。戻り値を呼ぶと終わりを知らせる。
   *
   * 切り替えは「セッション（JWT）を先に進めてから `/api/auth/me` を引き直す」順で走る。
   * その間、画面は前の組織・ワークスペースのままだが、**サーバはもう切り替え先で受け付ける**。
   * ここで前の画面から書き込むと、書いた内容が切り替え先へ入る（例: 新規ページの作成は
   * リクエスト本文ではなくセッションから所属を決める）。権限の新旧ではなく**書き込み先が
   * すり替わる**ので、画面側はこの合図で前の中身を落とす。
   */
  markContextMove: (destination: { organizationId?: string | null; workspaceId?: string | null }) => () => void;
}

export function useAuthSwitch({
  refreshUser,
  update,
  queryClient,
  conversationUIActions,
  markContextMove,
}: UseAuthSwitchArgs) {
  /**
   * 切り替え要求の世代（Issue #8030）。
   *
   * **最後の要求だけが状態を書く。** A への切り替え中に B が始まると、A は残りの副作用を
   * やめる（JWT は B が上書きするので、A が書き戻すと画面とセッションが食い違う）。
   */
  const switchGenerationRef = useRef(0);

  /**
   * 切り替え API の送信を**一本ずつ**流す列（Issue #8122）。
   *
   * 世代判定は応答を捨てるだけで、**DB の更新は取り消せない**。`/api/auth/switch-workspace`
   * は応答前に DB の現在ワークスペースを進めるので、A と B を並行に送ると、B の更新の後に
   * A の更新が着いて DB だけ A に戻る（画面と JWT は B）。前の送信が終わるまで次を送らない。
   */
  const switchRequestQueueRef = useRef<Promise<unknown>>(Promise.resolve());

  const switchOrganization = useCallback(async (organizationId: string) => {
    if (!organizationId || !/^[a-zA-Z0-9][a-zA-Z0-9\-_]{0,62}$/.test(organizationId)) {
      throw new Error('Invalid organization ID format');
    }

    // **叩く前に知らせる**（Issue #8129）。この API は DB の現在組織を進めるので、応答を
    // 待ってから知らせると、その間に前の画面から切り替え先へ書ける。失敗したときは
    // `finally` で戻すので、画面は元に戻る。
    const endContextMove = markContextMove({ organizationId });
    try {
      const response = await fetch('/api/auth/switch-organization', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ organizationId }),
      });

      if (!response.ok) {
        // **失敗しても実状態へ合わせ直す**（Issue #8175）。この API は DB の現在組織を
        // 更新したあとに利用者を取り直す。**更新は通って取り直しだけが落ちる**と 500 に
        // なるので、画面だけ前の組織のまま残る。サーバは各リクエストで DB の最新値を読む
        // ため、そこから書くと新しい組織へ入る（利用者は新組織でも権限を持つので弾かれない）。
        //
        // 引き直しても直らない（＝ DB は動いていない）場合もあるが、**引いた結果に合わせる**
        // のはどちらでも正しい。切り替えの成否は呼び出し元へそのまま失敗として返す。
        await refreshUser();
        throw new Error('Failed to switch organization');
      }

      // Issue #2691: 組織切替でWSも変わるため全キャッシュをクリア
      queryClient.clear();
      // Issue #4139: Valtio 側の会話一覧も前 Org/WS のものを残さない
      conversationUIActions.resetForWorkspaceSwitch();

      // **引けなかったら切り替えを成功にしない**（Issue #8076）。ワークスペース側と同じ理由で、
      // セッションは新しい組織を指しているのに画面の認証状態は古いまま、という食い違いを
      // 「成功」として返さない。
      //
      // ただし **1 回の失敗で切り替えを取り消さない**（レビュー指摘）。`/api/auth/switch-organization`
      // が通った時点で DB の現在組織は更新済みなので、一時的な取得失敗で reject すると
      // 「サーバは新組織・画面は旧組織・呼び出し元には失敗」という食い違いが残る。引き直す。
      if (await refreshUser() === 'unusable' && await refreshUser() === 'unusable') {
        throw new Error('組織の切り替え後にユーザー情報を取得できませんでした');
      }
      window.dispatchEvent(new CustomEvent('organizationChanged', { detail: { organizationId } }));
    } finally {
      endContextMove();
    }
  }, [refreshUser, queryClient, conversationUIActions, markContextMove]);


  /**
   * 切り替えが決まったあとの反映（JWT → キャッシュ削除 → ローカル状態 → イベント）。
   *
   * **一段ごとに追い越しを確かめる。** どれも `await` を挟むので、途中で新しい切り替えが
   * 始まり得る（Issue #8030）。`switchWorkspace` から出しているのは、本体の分岐が増えて
   * 1 つの関数で追いにくくなったため。
   */
  const applySwitchedWorkspace = useCallback(async (
    user: { currentOrganizationId: string; currentWorkspaceId: string },
    workspaceId: string | null,
    superseded: () => boolean,
  ): Promise<SwitchWorkspaceOutcome> => {
    // 印は `switchWorkspace` が持つ（レビュー指摘 P1）。ここで立てるのでは遅い ──
    // 切り替え API が DB の現在ワークスペースを進めたあとになる。
    await updateSessionWorkspace(update, user);

    // `update` の待ち時間にも追い越され得る。**キャッシュ削除より前にもう一度見る**
    // （`queryClient.clear()` は新しい切り替えが積んだキャッシュまで巻き添えにする）。
    if (superseded()) {
      logger.info('[switchWorkspace] superseded during JWT update; skipping cache reset');
      return 'superseded';
    }

    // Issue #2691: JWT 更新後・状態更新前に全 React Query キャッシュを削除
    // 旧 WS の in-flight レスポンスやキャッシュ残留による表示不整合を一括防止
    queryClient.clear();
    // Issue #4139: React Query には乗っていない Valtio 側の会話一覧 (conversationUiStore)
    // も前 WS のものを残さないようにクリアし、再フェッチを誘発する
    conversationUIActions.resetForWorkspaceSwitch();

    // ローカル状態を更新。**`await` の後で判定しても遅い**ので、setter の直前で
    // 見てもらう（Issue #8030 レビュー指摘）。`/api/auth/me` の応答は古い断面を
    // 持ち得るため、そのまま書くと新しい切り替えの表示が巻き戻る。
    const refreshed = await refreshUser(() => !superseded());

    // **引けなかったら切り替えを成功にしない**（Issue #8076 レビュー指摘）。
    // JWT は新しいワークスペースを指しているのに画面は古いまま、という食い違いを
    // 「成功」として返すと、呼び出し元がそのまま次の操作へ進む。
    if (refreshed === 'unusable') {
      throw new PostSwitchUserUnavailableError();
    }

    // イベントも同じ理由で出さない。受け手（画面）が古いワークスペースへ
    // 再取得を掛けてしまう。
    if (superseded()) {
      logger.info('[switchWorkspace] superseded during refreshUser; skipping workspaceChanged');
      return 'superseded';
    }

    window.dispatchEvent(new CustomEvent('workspaceChanged', { detail: { workspaceId } }));
    return 'switched';
  }, [refreshUser, update, queryClient, conversationUIActions]);

  /**
   * 切り替え API を叩き、**追い越しを段ごとに見ながら**適用まで進める（Issue #8030）。
   *
   * `await` の前後で必ず確かめる。どこか 1 つでも飛ばすと、古い要求が新しい要求の状態を
   * 上書きする ── JWT を書いてしまうと戻せないので、特に `update()` の前が要。
   */
  const requestAndApplySwitch = useCallback(async (
    workspaceId: string | null,
    superseded: () => boolean,
  ): Promise<SwitchWorkspaceOutcome> => {
    // Issue #659: API 呼び出しで DB 更新と更新後のユーザー情報取得を同時に行う
    // Issue #8122: 前の送信が終わってから送る。順番待ちの間に追い越されたら**送らない**
    // （DB を一度 A へ動かしてから B へ、という無駄な往復もしない）。
    const request = waitAtMost(switchRequestQueueRef.current, SWITCH_QUEUE_WAIT_MS).then(() => (superseded()
      ? null
      : fetch('/api/auth/switch-workspace', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workspaceId }),
      })));
    // 失敗しても列は止めない（次の要求は送る）。
    switchRequestQueueRef.current = request.catch(() => undefined);
    const response = await request;

    // **追い越されていたら、失敗も黙って捨てる**（Issue #8030 レビュー指摘）。
    // ここで throw すると、B が成功しているのに A の遅れてきた 403 / 500 で
    // 呼び出し元がエラーを出す。静かに取り消す、という意図と食い違う。
    if (superseded() || !response) {
      logger.info('[switchWorkspace] superseded before response handling; discarding result');
      return 'superseded';
    }
    throwForFailedWorkspaceSwitch(response);

    const data = await response.json();

    // **応答の解析も await 境界**（Issue #8030 レビュー指摘）。ここを見ないと、
    // 解析中に始まった B のあとで A が `update()` を呼び、JWT を A へ戻してしまう。
    // `update()` の後の判定では、書いてしまった JWT を戻せない。
    if (superseded()) {
      logger.info('[switchWorkspace] superseded while parsing response; skipping JWT update');
      return 'superseded';
    }
    // **JWT へ書く値は境界で確かめる**（レビュー指摘）。存在だけ見て渡すと、ID が欠けた／
    // 文字列でない応答がそのまま `session.update()` に届く。truthy な非文字列は JWT に
    // 載り得るので、あとから「どのワークスペースにいるのか」が壊れた状態になる。
    const switched = toSwitchedUser(data, workspaceId);
    if (!switched) {
      // **失敗の記録をここで残す**（レビュー指摘）。呼び出し元には画面へエラーを出すだけの
      // ものもあり、そこで握られると「なぜ切り替わらなかったのか」が運用側に残らない。
      // 応答本文は載せない（ID 以外の内容が入り得る）。
      const error = new Error('ユーザー情報の取得に失敗しました');
      logger.error('[switchWorkspace] invalid switch response', error, {
        operation: 'switchWorkspace',
        workspaceId,
      });
      throw error;
    }

    return await applySwitchedWorkspace(switched, workspaceId, superseded);
  }, [applySwitchedWorkspace]);

  /**
   * 切り替えが落ちたときの後始末（Issue #8030 / #8076）。
   *
   * **表示をセッションの実状態へ合わせ直す。** 追い越しの取り消しは「新しい要求が通る」
   * ことを前提に古い要求を止める。新しい方が 403 などで落ちると、古い要求は既に止まって
   * いるのに新しい方も適用されず、画面が途中の状態で取り残され得る。
   *
   * 追い越されている場合は `refreshUser` 側のガードが setter を止める（勝った側を上書き
   * しない）。
   */
  const recoverFromSwitchFailure = useCallback(async (
    error: unknown,
    workspaceId: string | null,
    superseded: () => boolean,
  ): Promise<SwitchWorkspaceOutcome> => {
    const retried = await refreshUser(() => !superseded());

    // **追い越されていたら、この失敗は呼び出し元に関係ない**（Issue #8030 レビュー指摘）。
    // 解析や JWT 更新が落ちても、勝った側が成功しているなら利用者に見せるエラーは無い。
    if (superseded()) {
      logger.info('[switchWorkspace] superseded before failing; discarding error');
      return 'superseded';
    }

    if (recoveredFromUnavailableUser(error, retried)) {
      window.dispatchEvent(new CustomEvent('workspaceChanged', { detail: { workspaceId } }));
      return 'switched';
    }

    // Issue #659: エラーを握りつぶさず、呼び出し元に伝播
    throw error;
  }, [refreshUser]);

  const switchWorkspace = useCallback(async (workspaceId: string | null) => {
    if (workspaceId && !/^[a-zA-Z0-9][a-zA-Z0-9\-_]{0,62}$/.test(workspaceId)) {
      throw new Error('Invalid workspace ID format');
    }

    // **この呼び出しの世代を確定させてから走り出す**（Issue #8030）。
    switchGenerationRef.current += 1;
    const generation = switchGenerationRef.current;
    /** 追い越されたか。追い越されていたら、そこから先の副作用は行わない。 */
    const superseded = () => switchGenerationRef.current !== generation;

    /**
     * **切り替え API を叩く前に立て、後始末が終わるまで外さない**（レビュー指摘 P1）。
     *
     * `/api/auth/switch-workspace` は応答を返す前に DB の現在ワークスペースを進め、
     * サーバは各リクエストでその最新値を読む（`session-builder.ts`）。応答を受けてから
     * 立てるのでは、**DB が動いてから印が立つまで**の間、前の画面から切り替え先へ書ける。
     * 応答が 500 や不正な形で返る経路では、印が一度も立たないままになる。
     *
     * 失敗の後始末（`recoverFromSwitchFailure` の引き直し）も内側に入れる。先に外すと、
     * サーバは切り替え先・画面は切り替え元のまま前の中身が戻り、そこから書ける。
     *
     * 行き先はまだ要求した `workspaceId` しか分からない（組織は応答で決まる）。画面は
     * 「動いている最中かどうか」だけを見るので、これで足りる。
     */
    const endContextMove = markContextMove({ workspaceId });
    try {
      try {
        return await requestAndApplySwitch(workspaceId, superseded);
      } catch (error) {
        return await recoverFromSwitchFailure(error, workspaceId, superseded);
      }
    } finally {
      // **必ず外す。** 残すと、切り替えが終わっても中身が戻らない。
      endContextMove();
    }
  }, [requestAndApplySwitch, recoverFromSwitchFailure, markContextMove]);

  return { switchOrganization, switchWorkspace };
}
